/**
 * Page and task search (combobox). Type, arrow to a result, Enter opens it; Escape clears.
 * Results come from searchItems() over the page registries; nothing leaves the browser.
 */

import { forwardRef, useId, useMemo, useState, type KeyboardEvent } from 'react';
import { ShellIcon } from './icons';
import { searchItems } from './search';
import type { SearchItem } from './types';

export interface SearchBoxProps {
  items: ReadonlyArray<SearchItem>;
  onNavigate: (item: SearchItem) => void;
  label: string;
  placeholder?: string;
  variant?: 'sidebar' | 'hero';
  /** Shows the Ctrl K hint. */
  shortcutHint?: boolean;
  limit?: number;
}

export const SearchBox = forwardRef<HTMLInputElement, SearchBoxProps>(function SearchBox(
  { items, onNavigate, label, placeholder, variant = 'sidebar', shortcutHint, limit = 8 },
  ref,
) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();
  const optId = (i: number): string => `${listId}-opt-${i}`;
  const results = useMemo(() => searchItems(items, q, limit), [items, q, limit]);
  const showList = open && q.trim().length > 0;

  const go = (item: SearchItem | undefined): void => {
    if (!item) return;
    setQ('');
    setOpen(false);
    setActive(0);
    onNavigate(item);
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (results.length ? (a + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (results.length ? (a - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter') {
      if (showList && results.length) {
        e.preventDefault();
        go(results[Math.min(active, results.length - 1)]);
      }
    } else if (e.key === 'Escape') {
      if (q) {
        e.preventDefault();
        e.stopPropagation();
        setQ('');
        setOpen(false);
      }
    }
  };

  return (
    <div className={`ps-search ps-search--${variant}`}>
      <div className="ps-search__field">
        <ShellIcon name="search" size={variant === 'hero' ? 20 : 16} className="ps-search__icon" />
        <input
          ref={ref}
          type="search"
          className="ps-search__input"
          role="combobox"
          aria-label={label}
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && results.length ? optId(Math.min(active, results.length - 1)) : undefined}
          placeholder={placeholder}
          value={q}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKey}
        />
        {shortcutHint && (
          <kbd className="ps-search__kbd" aria-hidden="true">
            Ctrl K
          </kbd>
        )}
      </div>
      <ul id={listId} role="listbox" aria-label={`${label} results`} className="ps-search__results" hidden={!showList}>
        {showList &&
          results.map((r, i) => (
            <li
              key={`${r.kind}|${r.path}|${r.title}`}
              id={optId(i)}
              role="option"
              aria-selected={i === active}
              className={`ps-search__result${i === active ? ' ps-search__result--active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault(); // keep focus in the input until we navigate
                go(r);
              }}
              onMouseEnter={() => setActive(i)}
            >
              <span className="ps-search__result-title">
                {r.title}
                {r.external && <ShellIcon name="external" size={12} />}
              </span>
              <span className="ps-search__result-where">
                <span className="ps-search__kind">{r.kind === 'task' ? 'Task' : 'Page'}</span> {r.crumb}
              </span>
            </li>
          ))}
        {showList && results.length === 0 && (
          <li className="ps-search__none" role="presentation">
            Nothing matches &ldquo;{q.trim()}&rdquo;. Try another word, or browse the sections in the menu.
          </li>
        )}
      </ul>
      <span className="ps-visually-hidden" aria-live="polite">
        {showList ? (results.length ? `${results.length} result${results.length === 1 ? '' : 's'}` : 'No results') : ''}
      </span>
    </div>
  );
});
